import { TOOLS, FLIPPED_HORIZONTALLY_FLAG, FLIPPED_VERTICALLY_FLAG, FLIPPED_DIAGONALLY_FLAG, MIN_BRUSH_SIZE, MAX_BRUSH_SIZE, STAMP_SLOTS } from '../utils/constants.js';

/**
 * Global keyboard shortcuts for the editor.
 *
 * Tool shortcuts:    B=Pencil, R=Rect, G=Fill, E=Eraser, S=Select,
 *                    P=Property, O=Object, T=Terrain
 * Tile transforms:   X=Flip H, Y=Flip V, Z=Rotate CW, Shift+Z=Rotate CCW
 * Brush:             [ = Smaller, ] = Bigger
 * Stamp memory:      Ctrl+1-9 = Store, 1-9 = Recall
 * Tile navigation:   Arrow keys = Move palette selection
 * View:              # or Ctrl+G = Toggle grid, +/- = Zoom, Ctrl+0 = Reset
 * Layers:            PageUp/PageDown
 * File:              Ctrl+O, Ctrl+S, Ctrl+Shift+S
 * Edit:              Ctrl+Z, Ctrl+Shift+Z/Ctrl+Y
 * Selection:         Ctrl+C, Ctrl+V, Delete, Escape
 */
export class KeyboardShortcuts {
  constructor(state, commandHistory, fileManager, compositor, toolManager) {
    this.state = state;
    this.commandHistory = commandHistory;
    this.fileManager = fileManager;
    this.compositor = compositor;
    this.toolManager = toolManager || null;

    // Stamp memory: 9 slots (Ctrl+1 through Ctrl+9)
    this._stampSlots = new Array(STAMP_SLOTS).fill(null);

    document.addEventListener('keydown', (e) => this._onKeyDown(e));
  }

  _onKeyDown(e) {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

    const ctrl = e.ctrlKey || e.metaKey;
    const key = e.key.toLowerCase();

    // ─── Shortcut overlay ────────────────────────────────────
    const inputFocused = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
    if (key === 'h' && !ctrl && !e.shiftKey && !inputFocused) {
      e.preventDefault();
      window.__tileforge?.shortcutOverlay?.toggle();
      return;
    }

    // ─── Command Palette ─────────────────────────────────────
    if (ctrl && key === 'p') {
      e.preventDefault();
      window.__tileforge?.commandPalette?.toggle();
      return;
    }

    // ─── File operations ──────────────────────────────────────
    if (ctrl && key === 'o') { e.preventDefault(); this.fileManager.openFile(); return; }
    if (ctrl && key === 's') {
      e.preventDefault();
      if (e.shiftKey) this.fileManager.saveFileAs();
      else this.fileManager.saveFile();
      return;
    }

    // ─── Undo/Redo ────────────────────────────────────────────
    if (ctrl && key === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.commandHistory.redo();
      else this.commandHistory.undo();
      this.compositor.requestRender();
      return;
    }
    if (ctrl && key === 'y') {
      e.preventDefault();
      this.commandHistory.redo();
      this.compositor.requestRender();
      return;
    }
    if (ctrl && key === 'h') {
      e.preventDefault();
      window.__tileforge?.undoHistoryPanel?.toggle();
      return;
    }

    // ─── Stamp memory: Ctrl+1-9 = Store, 1-9 = Recall ────────
    if (e.key >= '1' && e.key <= '9') {
      const slot = parseInt(e.key) - 1;
      if (ctrl) {
        // Store current selection
        e.preventDefault();
        this._stampSlots[slot] = {
          tileId: this.state.get('selectedTileId'),
          stamp: this.state.get('stamp'),
          randomTiles: this.state.get('randomTiles'),
        };
        this._showStatus(`Stamp stored in slot ${e.key}`);
        return;
      } else if (!e.shiftKey && !e.altKey) {
        // Recall
        const stored = this._stampSlots[slot];
        if (stored) {
          e.preventDefault();
          this.state.batch({
            selectedTileId: stored.tileId,
            stamp: stored.stamp,
            randomTiles: stored.randomTiles,
          });
          this._showStatus(`Stamp recalled from slot ${e.key}`);
          return;
        }
      }
    }

    // ─── Tile flip/rotate shortcuts ───────────────────────────
    if (key === 'x' && !ctrl) {
      e.preventDefault();
      this._toggleFlipFlag(FLIPPED_HORIZONTALLY_FLAG);
      this._showStatus(this._getFlipStatus());
      this.compositor.requestRender();
      return;
    }
    if (key === 'y' && !ctrl) {
      e.preventDefault();
      this._toggleFlipFlag(FLIPPED_VERTICALLY_FLAG);
      this._showStatus(this._getFlipStatus());
      this.compositor.requestRender();
      return;
    }
    if (key === 'z' && !ctrl) {
      e.preventDefault();
      if (e.shiftKey) {
        this._rotateCCW();
      } else {
        this._rotateCW();
      }
      this._showStatus(this._getFlipStatus());
      this.compositor.requestRender();
      return;
    }

    // ─── Brush size: [ = smaller, ] = bigger ──────────────────
    if (e.key === '[') {
      e.preventDefault();
      const size = Math.max(MIN_BRUSH_SIZE, (this.state.get('brushSize') || 1) - 1);
      this.state.set('brushSize', size);
      this._showStatus(`Brush: ${size}×${size}`);
      return;
    }
    if (e.key === ']') {
      e.preventDefault();
      const size = Math.min(MAX_BRUSH_SIZE, (this.state.get('brushSize') || 1) + 1);
      this.state.set('brushSize', size);
      this._showStatus(`Brush: ${size}×${size}`);
      return;
    }

    // ─── Tile palette navigation: Arrow keys / WASD ───────────
    if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown'].includes(key) && !ctrl) {
      e.preventDefault();
      this._navigatePalette(key);
      return;
    }

    // ─── Tool shortcuts ───────────────────────────────────────
    switch (key) {
      case 'b': this.state.set('activeTool', TOOLS.PENCIL); break;
      case 'r': this.state.set('activeTool', TOOLS.RECT); break;
      case 'g': if (!ctrl) this.state.set('activeTool', TOOLS.FILL); break;
      case 'e': this.state.set('activeTool', TOOLS.ERASER); break;
      case 's': if (!ctrl) this.state.set('activeTool', TOOLS.SELECT); break;
      case 'p': this.state.set('activeTool', TOOLS.PROPERTY); break;
      case 'o': if (!ctrl) this.state.set('activeTool', TOOLS.OBJECT); break;
      case 't': this.state.set('activeTool', TOOLS.TERRAIN); break;
      case 'd': this.state.set('activeTool', TOOLS.SCATTER); break;
      case 'l': this.state.set('activeTool', TOOLS.LINE); break;
    }

    // ─── View toggles ─────────────────────────────────────────
    if (e.key === '#' || (ctrl && key === 'g')) {
      e.preventDefault();
      this.state.set('showGrid', !this.state.get('showGrid'));
      this.compositor.requestRender();
    }

    // ─── Layer shortcuts ──────────────────────────────────────
    if (e.key === 'PageUp') {
      e.preventDefault();
      const idx = this.state.get('activeLayerIndex');
      const layers = this.state.get('layers');
      if (idx < layers.length - 1) this.state.set('activeLayerIndex', idx + 1);
    }
    if (e.key === 'PageDown') {
      e.preventDefault();
      const idx = this.state.get('activeLayerIndex');
      if (idx > 0) this.state.set('activeLayerIndex', idx - 1);
    }

    // ─── Copy/Paste/Delete for SelectTool ─────────────────────
    if (ctrl && key === 'c') {
      const selectTool = this._getSelectTool();
      if (selectTool && this.state.get('activeTool') === TOOLS.SELECT) {
        e.preventDefault();
        selectTool.copySelection();
        return;
      }
    }
    if (ctrl && key === 'v') {
      const selectTool = this._getSelectTool();
      if (selectTool) {
        e.preventDefault();
        this.state.set('activeTool', TOOLS.SELECT);
        selectTool.startPaste();
        return;
      }
    }
    if (e.key === 'Delete') {
      const selectTool = this._getSelectTool();
      if (selectTool && this.state.get('activeTool') === TOOLS.SELECT) {
        e.preventDefault();
        selectTool.deleteSelection();
        this.compositor.requestRender();
        return;
      }
    }
    if (e.key === 'Escape') {
      // Cancel paste or clear flip flags
      const selectTool = this._getSelectTool();
      if (selectTool && selectTool.isPasting()) {
        e.preventDefault();
        selectTool.cancelPaste();
        return;
      }
      // Clear flip flags
      if (this.state.get('tileFlipFlags')) {
        this.state.set('tileFlipFlags', 0);
        this._showStatus('Flip/rotate cleared');
        return;
      }
    }

    // ─── Zoom ─────────────────────────────────────────────────
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      const z = Math.min(4, this.state.get('zoom') + 0.25);
      this.state.set('zoom', z);
      this.compositor.requestRender();
    }
    if (e.key === '-' && !ctrl) {
      e.preventDefault();
      const z = Math.max(0.25, this.state.get('zoom') - 0.25);
      this.state.set('zoom', z);
      this.compositor.requestRender();
    }
    if (key === '0' && ctrl) {
      e.preventDefault();
      this.state.set('zoom', 1);
      this.compositor.requestRender();
    }
  }

  // ─── Flip/Rotate Helpers ─────────────────────────────────────

  _toggleFlipFlag(flag) {
    const current = this.state.get('tileFlipFlags') || 0;
    this.state.set('tileFlipFlags', current ^ flag);
  }

  _rotateCW() {
    // Rotate CW = toggle diagonal + toggle horizontal
    let flags = this.state.get('tileFlipFlags') || 0;
    flags ^= FLIPPED_DIAGONALLY_FLAG;
    flags ^= FLIPPED_HORIZONTALLY_FLAG;
    this.state.set('tileFlipFlags', flags);
  }

  _rotateCCW() {
    // Rotate CCW = toggle diagonal + toggle vertical
    let flags = this.state.get('tileFlipFlags') || 0;
    flags ^= FLIPPED_DIAGONALLY_FLAG;
    flags ^= FLIPPED_VERTICALLY_FLAG;
    this.state.set('tileFlipFlags', flags);
  }

  _getFlipStatus() {
    const flags = this.state.get('tileFlipFlags') || 0;
    const parts = [];
    if (flags & FLIPPED_HORIZONTALLY_FLAG) parts.push('FlipH');
    if (flags & FLIPPED_VERTICALLY_FLAG) parts.push('FlipV');
    if (flags & FLIPPED_DIAGONALLY_FLAG) parts.push('Diag');
    return parts.length > 0 ? `Transform: ${parts.join(' + ')}` : 'Transform: None';
  }

  // ─── Palette Navigation ──────────────────────────────────────

  _navigatePalette(key) {
    const tileId = this.state.get('selectedTileId') || 0;
    const tilesets = this.state.get('tilesets');
    const tsIndex = this.state.get('activeTilesetIndex') || 0;
    const ts = tilesets?.[tsIndex];
    if (!ts) return;

    const columns = ts.columns || Math.floor(ts.image.width / (ts.tileWidth || 32));
    let newId = tileId;

    switch (key) {
      case 'arrowleft':  newId = tileId - 1; break;
      case 'arrowright': newId = tileId + 1; break;
      case 'arrowup':    newId = tileId - columns; break;
      case 'arrowdown':  newId = tileId + columns; break;
    }

    const maxTile = (ts.tileCount || columns * Math.floor(ts.image.height / (ts.tileHeight || 32))) - 1;
    if (newId >= 0 && newId <= maxTile) {
      this.state.batch({
        selectedTileId: newId,
        stamp: null,
        randomTiles: null,
      });
    }
  }

  // ─── Utilities ───────────────────────────────────────────────

  _getSelectTool() {
    if (!this.toolManager) return null;
    return this.toolManager.getTool ? this.toolManager.getTool(TOOLS.SELECT) : null;
  }

  _showStatus(text) {
    const el = document.getElementById('status-tool');
    if (el) {
      el.textContent = text;
      // Reset after 2s
      clearTimeout(this._statusTimer);
      this._statusTimer = setTimeout(() => {
        const tool = this.state.get('activeTool');
        el.textContent = `Tool: ${tool.charAt(0).toUpperCase() + tool.slice(1)}`;
      }, 2000);
    }
  }
}
